import { Assets } from 'pixi.js';

export class SpineLoader {
  constructor() {
    this.spineAssets = new Map();
    this.loadedAssets = new Map();
  }

  async loadDefaultSpineAssets() {
    const spineAssets = [
      {
        name: 'bigwin',
        skeletonUrl: '/assets/pixi-scratch/Animations/bigwin/big_win.json',
        atlasUrl: '/assets/pixi-scratch/Animations/bigwin/big_win.atlas',
        textureUrl: '/assets/pixi-scratch/Animations/bigwin/big_win.webp'
      },
      // Add more assets here as needed
      {
        name: 'reveal_fx',
        skeletonUrl: '/assets/pixi-scratch/Animations/reveal_fx/reveal_fx.json',
        atlasUrl: '/assets/pixi-scratch/Animations/reveal_fx/reveal_fx.atlas',
        textureUrl: '/assets/pixi-scratch/Animations/reveal_fx/reveal_fx.webp'
      },
      {
        name: 'bg',
        skeletonUrl: '/assets/pixi-scratch/Animations/bg/bg.json',
        atlasUrl: '/assets/pixi-scratch/Animations/bg/bg.atlas',
        textureUrl: '/assets/pixi-scratch/Animations/bg/bg.webp'
      }
    ];
    await this.loadMultipleSpineAssets(spineAssets);
  }

  /**
   * Load Spine assets using the exact approach shared by the user
   * @param {string} name - Unique name for this spine asset
   * @param {string} skeletonUrl - Path to the .json skeleton file
   * @param {string} atlasUrl - Path to the .atlas file
   * @param {string} textureUrl - Path to the texture image (.webp, .png, etc.)
   */
  async loadSpineAsset(name, skeletonUrl, atlasUrl, textureUrl) {
    try {
      // Assets are loaded using PixiJS Assets.load() with aliases - EXACTLY as shared
      const loadedAssets = await Assets.load([
        {
          alias: `${name}Skeleton`,
          src: skeletonUrl,    // Spine skeleton data
        },
        {
          alias: `${name}Atlas`,
          src: atlasUrl,       // Atlas file defining texture regions
        },
        {
          alias: `${name}Texture`,
          src: textureUrl,     // Actual texture image
        },
      ]);

      // Store the loaded assets
      this.loadedAssets.set(name, {
        skeleton: loadedAssets[`${name}Skeleton`],
        atlas: loadedAssets[`${name}Atlas`],
        texture: loadedAssets[`${name}Texture`],
        aliases: {
          skeleton: `${name}Skeleton`,
          atlas: `${name}Atlas`,
          texture: `${name}Texture`
        }
      });

      console.log(`Spine asset "${name}" loaded successfully`);
      console.log('Loaded assets:', loadedAssets);
      return this.loadedAssets.get(name);

    } catch (error) {
      console.error(`Failed to load Spine asset "${name}":`, error);
      throw error;
    }
  }

  /**
   * Get loaded Spine asset data
   * @param {string} name - Name of the spine asset
   * @returns {Object|null} The loaded asset data or null if not found
   */
  getSpineAsset(name) {
    return this.loadedAssets.get(name) || null;
  }

  /**
   * Load multiple Spine assets at once
   * @param {Array} assets - Array of asset configurations
   */
  async loadMultipleSpineAssets(assets) {
    const loadPromises = assets.map(asset => 
      this.loadSpineAsset(asset.name, asset.skeletonUrl, asset.atlasUrl, asset.textureUrl)
    );

    try {
      await Promise.all(loadPromises);
      console.log(`Loaded ${assets.length} Spine assets successfully`);
    } catch (error) {
      console.error('Failed to load some Spine assets:', error);
      throw error;
    }
  }

  /**
   * Unload a specific Spine asset
   * @param {string} name - Name of the spine asset to unload
   */
  async unloadSpineAsset(name) {
    const assetData = this.loadedAssets.get(name);
    if (!assetData) {
      console.warn(`Spine asset "${name}" not found for unloading`);
      return;
    }

    try {
      // Unload all related assets
      await Assets.unload([
        assetData.aliases.skeleton,
        assetData.aliases.atlas,
        assetData.aliases.texture
      ]);

      // Remove from our tracking
      this.loadedAssets.delete(name);
      console.log(`Spine asset "${name}" unloaded successfully`);
    } catch (error) {
      console.error(`Failed to unload Spine asset "${name}":`, error);
    }
  }

  /**
   * Unload all Spine assets
   */
  async unloadAllSpineAssets() {
    const assetNames = Array.from(this.loadedAssets.keys());
    const unloadPromises = assetNames.map(name => this.unloadSpineAsset(name));

    try {
      await Promise.all(unloadPromises);
      console.log('All Spine assets unloaded successfully');
    } catch (error) {
      console.error('Failed to unload some Spine assets:', error);
    }
  }

  /**
   * Get list of all loaded Spine asset names
   * @returns {Array} Array of loaded asset names
   */
  getLoadedAssetNames() {
    return Array.from(this.loadedAssets.keys());
  }

  /**
   * Check if a Spine asset is loaded
   * @param {string} name - Name of the spine asset
   * @returns {boolean} True if loaded, false otherwise
   */
  isAssetLoaded(name) {
    return this.loadedAssets.has(name);
  }
}
